import { useCallback, useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Banner,
  BlockStack,
  Text,
  Divider,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { BulkFilterBar } from '../components/BulkOperations/BulkFilterBar';
import { BulkProgressBar } from '../components/BulkOperations/BulkProgressBar';
import { BulkApprovalList } from '../components/BulkOperations/BulkApprovalList';
import { PageEntrance } from '../components/ui';
import './BulkOperationsPage.css';
import {
  useBulkJobs,
  useBulkJobDetail,
  useCreateBulkJob,
  useApproveBulkProducts,
  useCancelBulkJob,
} from '../hooks/useBulkJob';
import type {
  BulkJobType,
  BulkFilterCriteria,
  BulkApprovalEntry,
} from '../types';

/**
 * T118 [US4]: BulkOperationsPage — filter, create job, track progress,
 * approve/reject, and navigate to history.
 *
 * @see contracts/bulk.md, spec US4
 */
export default function BulkOperationsPage() {
  const navigate = useNavigate();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const { data: jobList } = useBulkJobs();
  const { data: activeJob } = useBulkJobDetail(activeJobId);
  const createJob = useCreateBulkJob();
  const cancelJob = useCancelBulkJob();

  // Determine most recent active (QUEUED/RUNNING) job
  const runningJob = jobList?.jobs.find(
    (j) => j.status === 'QUEUED' || j.status === 'RUNNING',
  );

  // Auto-set active job when one is running
  useEffect(() => {
    if (runningJob && !activeJobId) {
      setActiveJobId(runningJob.id);
    }
  }, [runningJob, activeJobId]);

  const handleStartJob = useCallback(
    (jobType: BulkJobType, filter: BulkFilterCriteria) => {
      createJob.mutate(
        { jobType, filter, notifyEmail: true },
        {
          onSuccess: (data) => setActiveJobId(data.jobId),
        },
      );
    },
    [createJob],
  );

  const handleCancel = useCallback(() => {
    if (activeJobId) {
      cancelJob.mutate(activeJobId, {
        onSuccess: () => setActiveJobId(null),
      });
    }
  }, [activeJobId, cancelJob]);

  const handleDrillIn = useCallback(
    (productId: string) => {
      navigate(`/products/${productId}`);
    },
    [navigate],
  );

  const isJobActive = activeJob?.status === 'QUEUED' || activeJob?.status === 'RUNNING';
  const showApproval =
    activeJob?.status === 'COMPLETED' && activeJob.jobType === 'FIX_PREVIEW';

  return (
    <PageEntrance>
    <Page
      title="Bulk Operations"
      subtitle="Run operations across multiple products at once"
      secondaryActions={[
        { content: 'Job History', onAction: () => navigate('/bulk/history') },
      ]}
    >
      <BlockStack gap="400">
        {/* Hero CTA — sells the bulk ops feature */}
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">⚡ Fix and optimise products in bulk</h2>
            <p className="z-hero-subtitle">
              Run AI-powered scans, fix previews, and AI-Boost across your entire catalog — or filter to target specific products.
            </p>
          </div>
        </div>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {runningJob && !activeJobId && (
              <Banner tone="info" title="A bulk job is already running">
                <Text as="p" variant="bodySm">
                  You can monitor its progress or wait for it to complete.
                </Text>
              </Banner>
            )}

            {createJob.isError && (
              <Banner tone="critical" title="Failed to create job">
                <Text as="p" variant="bodySm">
                  {createJob.error?.message ?? 'Unknown error'}
                </Text>
              </Banner>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Filter & Start
                </Text>
                <BulkFilterBar
                  onStartJob={handleStartJob}
                  isLoading={createJob.isPending || !!runningJob}
                />
              </BlockStack>
            </Card>

            {activeJob && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Job Progress
                  </Text>
                  <BulkProgressBar
                    job={activeJob}
                    onCancel={isJobActive ? handleCancel : undefined}
                  />
                </BlockStack>
              </Card>
            )}

            {showApproval && activeJobId && (
              <ApprovalSection
                jobId={activeJobId}
                onDrillIn={handleDrillIn}
              />
            )}
          </BlockStack>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Bulk Processing</Text>
              <Divider />
              <Text as="p" variant="bodySm">
                Growth+ feature. Run scan, fix preview, apply, or
                export across filtered products as background jobs.
              </Text>
              <Text as="p" variant="bodySm">
                One job runs at a time. You can navigate away —
                you will be notified on completion.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      </BlockStack>
    </Page>
    </PageEntrance>
  );
}

function ApprovalSection({
  jobId,
  onDrillIn,
}: Readonly<{
  jobId: string;
  onDrillIn: (productId: string) => void;
}>) {
  const approveMutation = useApproveBulkProducts(jobId);
  const { data: job } = useBulkJobDetail(jobId);

  const handleApprove = useCallback(
    (approvals: BulkApprovalEntry[]) => {
      approveMutation.mutate({ approvals });
    },
    [approveMutation],
  );

  // Mock product rows from job data — real implementation would
  // fetch fix previews associated with the job
  const products = Array.from(
    { length: job?.successCount ?? 0 },
    (_, i) => ({
      id: `row-${i}`,
      productId: `product-${i}`,
      productTitle: `Product ${i + 1}`,
      status: 'PENDING' as const,
    }),
  );

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          Review & Approve
        </Text>
        <BulkApprovalList
          products={products}
          errors={job?.errorDetails}
          onApprove={handleApprove}
          onDrillIn={onDrillIn}
          isLoading={approveMutation.isPending}
        />
      </BlockStack>
    </Card>
  );
}
